/**
 * 🔥 SECRET DEALS ADVANCED SERVICE
 * Premium features for high-performance deal management
 * 
 * Features:
 * - Deal Readiness Score (seller evaluation)
 * - HOT DEAL detection & badges
 * - Deal Match Score (buyer-fit)
 * - Risk Flag AI
 * - Intent tracking
 * - First Look Access system
 */

import { db } from '../firebase-config';
import { collection, addDoc, updateDoc, doc, getDoc, query, where, orderBy, limit, getDocs, serverTimestamp, increment, arrayUnion } from 'firebase/firestore';

// =====================================================
// 1. DEAL READINESS SCORE (Eladó értékelése)
// =====================================================

/**
 * Calculate seller readiness score (0-100)
 * Evaluates: seriousness, urgency, price realism
 */
export const calculateDealReadinessScore = async (propertyId, sellerData) => {
  const scores = {
    seriousness: 0,    // 0-35 points
    urgency: 0,        // 0-35 points
    priceRealism: 0,   // 0-30 points
  };

  // 1. SERIOUSNESS (35 points)
  // Documentation completeness
  if (sellerData.hasTitle) scores.seriousness += 10;
  if (sellerData.hasPhotos) scores.seriousness += 8;
  if (sellerData.hasSurvey) scores.seriousness += 7;
  if (sellerData.hasLegalCheck) scores.seriousness += 5;
  if (sellerData.hasPriceAnalysis) scores.seriousness += 5;

  // 2. URGENCY (35 points)
  const timeline = sellerData.saleTimeline;
  if (timeline === 'immediate') scores.urgency = 35;
  else if (timeline === '1_month') scores.urgency = 28;
  else if (timeline === '3_months') scores.urgency = 20;
  else if (timeline === '6_months') scores.urgency = 12;
  else scores.urgency = 5;

  // Response time bonus
  if (sellerData.avgResponseTime < 2) scores.urgency += 5; // < 2 hours
  else if (sellerData.avgResponseTime < 24) scores.urgency += 3; // < 24 hours

  // 3. PRICE REALISM (30 points)
  const marketPrice = sellerData.marketPrice || 0;
  const askingPrice = sellerData.askingPrice || 0;
  
  if (marketPrice > 0 && askingPrice > 0) {
    const priceRatio = askingPrice / marketPrice;
    if (priceRatio >= 0.95 && priceRatio <= 1.05) scores.priceRealism = 30; // +/- 5%
    else if (priceRatio >= 0.90 && priceRatio <= 1.10) scores.priceRealism = 24; // +/- 10%
    else if (priceRatio >= 0.85 && priceRatio <= 1.15) scores.priceRealism = 18; // +/- 15%
    else if (priceRatio >= 0.80 && priceRatio <= 1.20) scores.priceRealism = 12; // +/- 20%
    else scores.priceRealism = 5; // Way off
  }

  // Price reduction history bonus
  if (sellerData.priceReductions > 0 && sellerData.priceReductions < 3) {
    scores.priceRealism += 5; // Flexible but not desperate
  }

  const totalScore = scores.seriousness + scores.urgency + scores.priceRealism;

  // Return detailed breakdown
  return {
    totalScore: Math.min(100, totalScore),
    breakdown: scores,
    grade: getReadinessGrade(totalScore),
    flags: getReadinessFlags(scores, sellerData),
    recommendation: getReadinessRecommendation(totalScore)
  };
};

const getReadinessGrade = (score) => {
  if (score >= 85) return { label: 'A+', color: 'green', text: '🟢 Készen áll' };
  if (score >= 70) return { label: 'A', color: 'green', text: '🟢 Nagyon kész' };
  if (score >= 55) return { label: 'B', color: 'yellow', text: '🟡 Közepes' };
  if (score >= 40) return { label: 'C', color: 'orange', text: '🟠 Gyenge' };
  return { label: 'D', color: 'red', text: '🔴 Nem kész' };
};

const getReadinessFlags = (scores, data) => {
  const flags = [];
  if (scores.seriousness < 20) flags.push('incomplete_docs');
  if (scores.urgency < 15) flags.push('not_urgent');
  if (scores.priceRealism < 15) flags.push('unrealistic_price');
  if (data.avgResponseTime > 48) flags.push('slow_response');
  return flags;
};

const getReadinessRecommendation = (score) => {
  if (score >= 85) return 'Azonnal kapcsolatfelvétel - készen áll a deal-re';
  if (score >= 70) return 'Magas prioritás - aktívan keresi a vevőt';
  if (score >= 55) return 'Közepes prioritás - valós deal lehet';
  if (score >= 40) return 'Alacsony prioritás - további munka szükséges';
  return 'Nem érdemes foglalkozni - nem komoly eladó';
};

// =====================================================
// 2. HOT DEAL DETECTION (AI + Manual)
// =====================================================

/**
 * Detect HOT DEAL based on multiple factors
 */
export const detectHotDeal = async (propertyData, marketData) => {
  const hotFactors = [];
  let hotScore = 0;

  // 1. Underpriced (30 points)
  const marketPrice = marketData.averagePrice || propertyData.marketPrice;
  const askingPrice = propertyData.price;
  if (marketPrice > 0) {
    const discount = ((marketPrice - askingPrice) / marketPrice) * 100;
    if (discount >= 20) { hotScore += 30; hotFactors.push('🔥 Alulárazott -20%+'); }
    else if (discount >= 15) { hotScore += 25; hotFactors.push('⚡ Alulárazott -15%'); }
    else if (discount >= 10) { hotScore += 20; hotFactors.push('💰 Alulárazott -10%'); }
    else if (discount >= 5) { hotScore += 15; hotFactors.push('📉 Alulárazott -5%'); }
  }

  // 2. Fast sale indicator (25 points)
  const daysOnMarket = propertyData.daysOnMarket || 0;
  if (daysOnMarket === 0) { hotScore += 25; hotFactors.push('🆕 Új listing - ma került fel'); }
  else if (daysOnMarket <= 3) { hotScore += 20; hotFactors.push('📅 Új - 3 nap alatt'); }
  else if (daysOnMarket <= 7) { hotScore += 15; hotFactors.push('📅 Friss - 1 hét alatt'); }

  // 3. High interest (20 points)
  const viewCount = propertyData.viewCount || 0;
  if (viewCount > 100) { hotScore += 20; hotFactors.push('👀 Nagy érdeklődés (100+)'); }
  else if (viewCount > 50) { hotScore += 15; hotFactors.push('📊 Magas érdeklődés (50+)'); }
  else if (viewCount > 20) { hotScore += 10; hotFactors.push('📈 Jó érdeklődés (20+)'); }

  // 4. ROI potential (25 points)
  if (propertyData.roiPotential) {
    if (propertyData.roiPotential >= 20) { hotScore += 25; hotFactors.push('💎 20%+ ROI'); }
    else if (propertyData.roiPotential >= 15) { hotScore += 20; hotFactors.push('💎 15%+ ROI'); }
    else if (propertyData.roiPotential >= 10) { hotScore += 15; hotFactors.push('📊 10%+ ROI'); }
  }

  // 5. Quick close ready (bonus 10)
  if (propertyData.quickCloseReady) {
    hotScore += 10;
    hotFactors.push('⚡ Gyors zárás kész');
  }

  // Determine HOT level
  let isHot = false;
  let hotLevel = 'normal';
  let badge = null;

  if (hotScore >= 70) {
    isHot = true;
    hotLevel = 'extreme';
    badge = {
      type: 'HOT',
      level: 'extreme',
      label: '🔥🔥🔥 HOT DEAL',
      color: 'from-red-600 via-orange-500 to-red-600',
      animation: 'animate-pulse',
      priority: 1
    };
  } else if (hotScore >= 50) {
    isHot = true;
    hotLevel = 'high';
    badge = {
      type: 'HOT',
      level: 'high',
      label: '🔥🔥 HOT DEAL',
      color: 'from-orange-500 to-red-500',
      animation: 'animate-pulse',
      priority: 2
    };
  } else if (hotScore >= 35) {
    isHot = true;
    hotLevel = 'medium';
    badge = {
      type: 'HOT',
      level: 'medium',
      label: '🔥 HOT',
      color: 'from-orange-400 to-orange-600',
      animation: '',
      priority: 3
    };
  }

  return {
    isHot,
    hotScore,
    hotLevel,
    badge,
    factors: hotFactors,
    expiresAt: isHot ? new Date(Date.now() + 48 * 60 * 60 * 1000) : null, // 48h hot status
    urgencyText: isHot ? '⚠️ Gyorsan cselekedj - ez a deal nem marad sokáig!' : null
  };
};

/**
 * Manual HOT DEAL marking by admin
 */
export const markAsHotDeal = async (propertyId, adminId, reason) => {
  const hotData = {
    propertyId,
    markedBy: adminId,
    markedAt: serverTimestamp(),
    reason,
    type: 'manual',
    expiresAt: new Date(Date.now() + 48 * 60 * 60 * 1000), // 48 hours
    isActive: true
  };

  await addDoc(collection(db, 'hot_deals'), hotData);

  // Update property
  await updateDoc(doc(db, 'properties', propertyId), {
    isHotDeal: true,
    hotMarkedAt: serverTimestamp(),
    hotExpiresAt: hotData.expiresAt,
    hotReason: reason,
    hotType: 'manual'
  });

  return { success: true, expiresAt: hotData.expiresAt };
};

// =====================================================
// 3. DEAL MATCH SCORE (Buyer-Fit AI)
// =====================================================

/**
 * Calculate how well a deal matches a specific buyer (0-100)
 */
export const calculateDealMatchScore = async (buyerId, propertyId) => {
  // Get buyer profile
  const buyerDoc = await getDoc(doc(db, 'users', buyerId));
  const buyer = buyerDoc.data();

  // Get property data
  const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
  const property = propertyDoc.data();

  const matchFactors = {
    price: 0,        // 0-25 points
    location: 0,     // 0-25 points
    type: 0,         // 0-20 points
    size: 0,         // 0-15 points
    roi: 0,          // 0-15 points
  };

  // 1. PRICE MATCH (25 points)
  const buyerMax = buyer.preferences?.maxPrice || 999999999;
  const buyerMin = buyer.preferences?.minPrice || 0;
  const propertyPrice = property.price;

  if (propertyPrice <= buyerMax && propertyPrice >= buyerMin) {
    matchFactors.price = 25;
  } else if (propertyPrice <= buyerMax * 1.1) {
    matchFactors.price = 20; // Within 10% of max
  } else if (propertyPrice <= buyerMax * 1.2) {
    matchFactors.price = 15; // Within 20% of max
  } else if (propertyPrice <= buyerMax * 1.3) {
    matchFactors.price = 10; // Within 30% of max
  }

  // 2. LOCATION MATCH (25 points)
  const preferredLocations = buyer.preferences?.locations || [];
  const propertyLocation = property.location;

  if (preferredLocations.includes(propertyLocation)) {
    matchFactors.location = 25;
  } else if (isNearby(propertyLocation, preferredLocations)) {
    matchFactors.location = 20;
  } else if (isSameRegion(propertyLocation, preferredLocations)) {
    matchFactors.location = 15;
  }

  // 3. TYPE MATCH (20 points)
  const preferredTypes = buyer.preferences?.propertyTypes || [];
  if (preferredTypes.includes(property.type)) {
    matchFactors.type = 20;
  } else if (isSimilarType(property.type, preferredTypes)) {
    matchFactors.type = 15;
  }

  // 4. SIZE MATCH (15 points)
  const preferredSize = buyer.preferences?.area_m2 || {};
  const propertySize = property.area_m2;

  if (preferredSize.min && preferredSize.max) {
    if (propertySize >= preferredSize.min && propertySize <= preferredSize.max) {
      matchFactors.size = 15;
    } else if (propertySize >= preferredSize.min * 0.9 && propertySize <= preferredSize.max * 1.1) {
      matchFactors.size = 12;
    }
  }

  // 5. ROI MATCH (15 points)
  if (buyer.preferences?.minROI && property.roiPotential) {
    if (property.roiPotential >= buyer.preferences.minROI + 5) {
      matchFactors.roi = 15;
    } else if (property.roiPotential >= buyer.preferences.minROI) {
      matchFactors.roi = 12;
    } else if (property.roiPotential >= buyer.preferences.minROI - 5) {
      matchFactors.roi = 8;
    }
  }

  const totalScore = Object.values(matchFactors).reduce((a, b) => a + b, 0);

  // Generate match reasons
  const matchReasons = generateMatchReasons(matchFactors, property, buyer);

  // Calculate deal strength
  const dealStrength = totalScore >= 85 ? 'perfect' : 
                      totalScore >= 70 ? 'strong' : 
                      totalScore >= 55 ? 'good' : 
                      totalScore >= 40 ? 'moderate' : 'weak';

  return {
    score: totalScore,
    factors: matchFactors,
    reasons: matchReasons,
    strength: dealStrength,
    recommendation: getMatchRecommendation(totalScore, property, buyer),
    shouldAlert: totalScore >= 70 // Alert buyer if score >= 70
  };
};

const generateMatchReasons = (factors, property, buyer) => {
  const reasons = [];
  if (factors.price >= 20) reasons.push('💰 Árban passzol a keresésedhez');
  if (factors.location >= 20) reasons.push('📍 Kívánt lokációban van');
  if (factors.type >= 15) reasons.push('🏠 A kívánt ingatlantípus');
  if (factors.size >= 12) reasons.push('📐 Megfelelő méret');
  if (factors.roi >= 12) reasons.push('📈 Elvárt ROI-t hozza');
  return reasons;
};

const getMatchRecommendation = (score, property, buyer) => {
  if (score >= 85) return '🔥 TÖKÉLETES MATCH! Azonnal vedd fel a kapcsolatot!';
  if (score >= 70) return '✨ Erős passzolás. Érdemes megfontolni.';
  if (score >= 55) return '👍 Jó lehetőség, nézd meg a részleteket.';
  if (score >= 40) return '🤔 Közepes passzolás, de lehet jó deal.';
  return '❌ Nem passzol a keresésedhez.';
};

const isNearby = (location, preferredList) => {
  // Simplified - would use actual distance calculation
  return preferredList.some(pref => location.includes(pref) || pref.includes(location));
};

const isSameRegion = (location, preferredList) => {
  // Simplified - would use actual region matching
  return true; // Placeholder
};

const isSimilarType = (type, preferredTypes) => {
  const similarMap = {
    'house': ['villa', 'family_home'],
    'apartment': ['flat', 'condo'],
    'land': ['plot', 'development_site']
  };
  return similarMap[type]?.some(t => preferredTypes.includes(t)) || false;
};

// =====================================================
// 4. INTENT BUTTON SYSTEM
// =====================================================

export const INTENT_LEVELS = {
  BROWSING: { value: 1, label: 'Böngészés', icon: '👀' },
  INTERESTED: { value: 2, label: 'Érdekel', icon: '🤔' },
  SERIOUSLY_INTERESTED: { value: 3, label: 'Komolyan érdekel', icon: '🔥' },
  WANT_TO_TALK: { value: 4, label: 'Beszélnék róla', icon: '💬' },
  MAKE_OFFER: { value: 5, label: 'Ajánlatot tennék', icon: '💰' }
};

/**
 * Record buyer intent on a property
 */
export const recordIntent = async (buyerId, propertyId, intentLevel, notes = '') => {
  const intentData = {
    buyerId,
    propertyId,
    intentLevel: INTENT_LEVELS[intentLevel]?.value || 1,
    intentLabel: INTENT_LEVELS[intentLevel]?.label || 'Böngészés',
    notes,
    recordedAt: serverTimestamp(),
    status: 'active',
    lastUpdated: serverTimestamp()
  };

  // Add to intents collection
  const intentRef = await addDoc(collection(db, 'buyer_intents'), intentData);

  // Update property stats
  await updateDoc(doc(db, 'properties', propertyId), {
    intentCount: increment(1),
    [`intentLevels.${intentLevel}`]: increment(1),
    lastIntentAt: serverTimestamp()
  });

  // If high intent, create alert for seller
  if (intentLevel === 'MAKE_OFFER' || intentLevel === 'WANT_TO_TALK') {
    await createHighIntentAlert(propertyId, buyerId, intentLevel);
  }

  return { 
    success: true, 
    intentId: intentRef.id,
    priority: getIntentPriority(intentLevel)
  };
};

const getIntentPriority = (intentLevel) => {
  const priorities = {
    'MAKE_OFFER': 1,      // Highest
    'WANT_TO_TALK': 2,
    'SERIOUSLY_INTERESTED': 3,
    'INTERESTED': 4,
    'BROWSING': 5
  };
  return priorities[intentLevel] || 5;
};

const createHighIntentAlert = async (propertyId, buyerId, intentLevel) => {
  await addDoc(collection(db, 'high_intent_alerts'), {
    propertyId,
    buyerId,
    intentLevel,
    alertType: 'high_intent',
    status: 'pending',
    createdAt: serverTimestamp(),
    requiresAction: true,
    message: `🔥 Komoly érdeklődő: ${INTENT_LEVELS[intentLevel]?.label}`
  });
};

/**
 * Get prioritized buyer list for a property
 */
export const getPrioritizedBuyers = async (propertyId, limit_count = 10) => {
  const q = query(
    collection(db, 'buyer_intents'),
    where('propertyId', '==', propertyId),
    where('status', '==', 'active'),
    orderBy('intentLevel', 'desc'),
    orderBy('recordedAt', 'desc'),
    limit(limit_count)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
    priority: getIntentPriorityFromValue(doc.data().intentLevel)
  }));
};

const getIntentPriorityFromValue = (value) => {
  if (value >= 5) return { rank: 1, label: 'Kritikus', color: 'red' };
  if (value >= 4) return { rank: 2, label: 'Magas', color: 'orange' };
  if (value >= 3) return { rank: 3, label: 'Közepes', color: 'yellow' };
  if (value >= 2) return { rank: 4, label: 'Alacsony', color: 'blue' };
  return { rank: 5, label: 'Böngészés', color: 'gray' };
};

// =====================================================
// 5. FIRST LOOK ACCESS SYSTEM
// =====================================================

/**
 * Grant First Look access to top buyers
 */
export const grantFirstLookAccess = async (propertyId, buyerIds, durationHours = 48) => {
  const expiresAt = new Date(Date.now() + durationHours * 60 * 60 * 1000);

  const firstLookData = {
    propertyId,
    buyerIds,
    grantedAt: serverTimestamp(),
    expiresAt,
    durationHours,
    status: 'active',
    accessType: 'exclusive',
    isPublic: false
  };

  const firstLookRef = await addDoc(collection(db, 'first_look_access'), firstLookData);

  // Update property
  await updateDoc(doc(db, 'properties', propertyId), {
    firstLookActive: true,
    firstLookExpiresAt: expiresAt,
    firstLookBuyers: buyerIds,
    visibility: 'exclusive' // Hidden from public
  });

  // Send notifications to privileged buyers
  await notifyFirstLookBuyers(buyerIds, propertyId, expiresAt);

  return {
    success: true,
    firstLookId: firstLookRef.id,
    expiresAt,
    message: `🔥 First Look hozzáférés aktiválva ${buyerIds.length} befektetőnek!`
  };
};

const notifyFirstLookBuyers = async (buyerIds, propertyId, expiresAt) => {
  // This would integrate with email/push/WhatsApp services
  for (const buyerId of buyerIds) {
    await addDoc(collection(db, 'notifications'), {
      userId: buyerId,
      type: 'first_look',
      propertyId,
      title: '🔥 EXKLUZÍV: Új deal elérhető!',
      message: 'Csak te és néhány kiválasztott befektető látja ezt most. 48 órád van!',
      expiresAt,
      status: 'pending',
      createdAt: serverTimestamp(),
      priority: 'high'
    });
  }
};

/**
 * Check if user has First Look access
 */
export const checkFirstLookAccess = async (propertyId, userId) => {
  const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
  const property = propertyDoc.data();

  // Check if First Look expired
  if (property.firstLookExpiresAt?.toDate() < new Date()) {
    // Expired - make public
    await updateDoc(doc(db, 'properties', propertyId), {
      firstLookActive: false,
      visibility: 'public'
    });
    return { hasAccess: true, isPublic: true, message: 'Már publikus' };
  }

  // Check if user is in exclusive list
  const hasExclusiveAccess = property.firstLookBuyers?.includes(userId);

  return {
    hasAccess: hasExclusiveAccess || !property.firstLookActive,
    isExclusive: property.firstLookActive,
    isPublic: !property.firstLookActive,
    expiresAt: property.firstLookExpiresAt,
    message: hasExclusiveAccess 
      ? '🔥 Van First Look hozzáférésed!'
      : property.firstLookActive 
        ? '⏳ Még exkluzív - kérj hozzáférést'
        : 'Már publikus'
  };
};

// =====================================================
// 6. RISK FLAG AI
// =====================================================

export const RISK_TYPES = {
  OVERPRICED: { level: 'high', label: '💸 Túlárazott', icon: '💸' },
  LEGAL_ISSUE: { level: 'high', label: '⚖️ Jogi probléma', icon: '⚖️' },
  LOCATION_RISK: { level: 'medium', label: '📍 Lokáció kockázat', icon: '📍' },
  SLOW_SALE: { level: 'medium', label: '🐌 Lassú eladás', icon: '🐌' },
  NEEDS_RENOVATION: { level: 'low', label: '🔨 Felújítás kell', icon: '🔨' },
  MARKET_DECLINE: { level: 'high', label: '📉 Piaci csökkenés', icon: '📉' }
};

/**
 * AI Risk Analysis for a property
 */
export const analyzePropertyRisk = async (propertyData, marketData) => {
  const risks = [];
  let riskScore = 0; // 0-100, higher = riskier

  // 1. Price risk analysis
  if (propertyData.price && marketData.averagePrice) {
    const priceRatio = propertyData.price / marketData.averagePrice;
    if (priceRatio > 1.3) {
      risks.push({ ...RISK_TYPES.OVERPRICED, reason: `30%+ a piaci átlag fölött` });
      riskScore += 25;
    } else if (priceRatio > 1.2) {
      risks.push({ ...RISK_TYPES.OVERPRICED, reason: `20%+ a piaci átlag fölött` });
      riskScore += 15;
    }
  }

  // 2. Days on market risk
  const daysOnMarket = propertyData.daysOnMarket || 0;
  if (daysOnMarket > 180) {
    risks.push({ ...RISK_TYPES.SLOW_SALE, reason: '6+ hónapja a piacon' });
    riskScore += 20;
  } else if (daysOnMarket > 90) {
    risks.push({ ...RISK_TYPES.SLOW_SALE, reason: '3+ hónapja a piacon' });
    riskScore += 10;
  }

  // 3. ROI risk
  if (propertyData.roiPotential && propertyData.roiPotential < 5) {
    risks.push({ ...RISK_TYPES.MARKET_DECLINE, reason: 'Alacsony ROI (<5%)' });
    riskScore += 15;
  }

  // 4. Location risk (would need external data)
  if (marketData.locationTrend === 'declining') {
    risks.push({ ...RISK_TYPES.LOCATION_RISK, reason: 'Csökkenő trend a környéken' });
    riskScore += 15;
  }

  // 5. Condition risk
  if (propertyData.condition === 'needs_renovation') {
    risks.push({ ...RISK_TYPES.NEEDS_RENOVATION, reason: 'Felújítást igényel' });
    riskScore += 10;
  }

  // Calculate risk level
  let riskLevel = 'low';
  if (riskScore >= 60) riskLevel = 'high';
  else if (riskScore >= 35) riskLevel = 'medium';

  return {
    riskScore,
    riskLevel,
    risks,
    isSafe: riskScore < 35,
    requiresCaution: riskScore >= 35 && riskScore < 60,
    avoid: riskScore >= 60,
    recommendation: getRiskRecommendation(riskLevel, risks)
  };
};

const getRiskRecommendation = (riskLevel, risks) => {
  if (riskLevel === 'high') return '⚠️ MAGAS KOCKÁZAT! Alaposan vizsgáld meg mielőtt döntesz.';
  if (riskLevel === 'medium') return '🤔 Közepes kockázat - tedd fel a kérdéseidet.';
  return '✅ Alacsony kockázat - normális ügyletnek tűnik.';
};

// =====================================================
// 7. DEAL ALERT SYSTEM
// =====================================================

/**
 * Create AI-powered deal alert for matching buyers
 */
export const createDealAlert = async (propertyId, alertType = 'new_deal') => {
  const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
  const property = propertyDoc.data();

  // Find matching buyers
  const matchingBuyers = await findMatchingBuyers(property);

  const alerts = [];

  for (const buyer of matchingBuyers) {
    const matchScore = await calculateDealMatchScore(buyer.id, propertyId);

    if (matchScore.score >= 60) {
      const alert = {
        buyerId: buyer.id,
        propertyId,
        type: alertType,
        matchScore: matchScore.score,
        reasons: matchScore.reasons,
        status: 'pending',
        channels: buyer.notificationPreferences || ['email', 'push'],
        sentAt: null,
        createdAt: serverTimestamp(),
        priority: matchScore.score >= 80 ? 'high' : 'normal',
        message: generateAlertMessage(property, matchScore)
      };

      const alertRef = await addDoc(collection(db, 'deal_alerts'), alert);
      alerts.push({ id: alertRef.id, ...alert });

      // Send notifications
      await sendDealAlert(buyer, alert);
    }
  }

  return {
    success: true,
    alertsCreated: alerts.length,
    alerts
  };
};

const findMatchingBuyers = async (property) => {
  // Query buyers with matching preferences
  const q = query(
    collection(db, 'users'),
    where('role', '==', 'buyer'),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs
    .map(doc => ({ id: doc.id, ...doc.data() }))
    .filter(buyer => {
      // Basic filtering
      const prefs = buyer.preferences || {};
      const maxPrice = prefs.maxPrice || 999999999;
      const locations = prefs.locations || [];

      return property.price <= maxPrice * 1.2 || // Within 20% of max
             locations.some(loc => property.location?.includes(loc));
    });
};

const generateAlertMessage = (property, matchScore) => {
  if (matchScore.score >= 85) {
    return `🔥 TÖKÉLETES MATCH! ${property.type} ${property.location} - ${matchScore.reasons[0]}`;
  }
  if (matchScore.score >= 70) {
    return `✨ Erős passzolás: ${property.type} ${property.location}`;
  }
  return `👍 Jó lehetőség: ${property.type} ${property.location}`;
};

const sendDealAlert = async (buyer, alert) => {
  const channels = alert.channels || [];

  // Push notification
  if (channels.includes('push')) {
    // Would integrate with Firebase Cloud Messaging
    console.log('📱 Push sent to:', buyer.id);
  }

  // Email
  if (channels.includes('email')) {
    // Would integrate with email service
    console.log('📧 Email sent to:', buyer.email);
  }

  // WhatsApp
  if (channels.includes('whatsapp') && buyer.phone) {
    // Would integrate with WhatsApp Business API
    console.log('💬 WhatsApp sent to:', buyer.phone);
  }

  // Update alert status
  await updateDoc(doc(db, 'deal_alerts', alert.id), {
    sentAt: serverTimestamp(),
    status: 'sent'
  });
};

// =====================================================
// 8. OFF-MARKET CERTIFIED BADGE
// =====================================================

/**
 * Validate and certify an off-market deal
 */
export const certifyOffMarket = async (propertyId, validatorId) => {
  // Perform validation checks
  const validationChecks = await performValidationChecks(propertyId);

  if (validationChecks.passed) {
    const certificationData = {
      propertyId,
      certifiedBy: validatorId,
      certifiedAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), // 90 days
      badge: {
        type: 'OFF_MARKET_CERTIFIED',
        label: '🔒 OFF-MARKET CERTIFIED',
        color: 'from-blue-600 to-indigo-600',
        icon: '🔒'
      },
      validationDetails: validationChecks,
      isActive: true
    };

    const certRef = await addDoc(collection(db, 'off_market_certifications'), certificationData);

    // Update property
    await updateDoc(doc(db, 'properties', propertyId), {
      offMarketCertified: true,
      certificationId: certRef.id,
      certificationExpiresAt: certificationData.expiresAt
    });

    return {
      success: true,
      certificationId: certRef.id,
      badge: certificationData.badge,
      message: '✅ Off-market validálás sikeres!'
    };
  }

  return {
    success: false,
    errors: validationChecks.errors,
    message: '❌ Validálás sikertelen - lásd hibák'
  };
};

const performValidationChecks = async (propertyId) => {
  const propertyDoc = await getDoc(doc(db, 'properties', propertyId));
  const property = propertyDoc.data();

  const checks = {
    hasValidTitle: !!property.titleStatus && property.titleStatus === 'clear',
    hasLegalCheck: !!property.legalVerified,
    hasPriceVerification: !!property.priceVerified,
    hasOwnerVerification: !!property.ownerVerified,
    notListedPublicly: !property.publiclyListed,
    errors: []
  };

  if (!checks.hasValidTitle) checks.errors.push('Nem tisztázott tulajdoni lap');
  if (!checks.hasLegalCheck) checks.errors.push('Nincs jogi ellenőrzés');
  if (!checks.hasPriceVerification) checks.errors.push('Ár nincs verifikálva');
  if (!checks.hasOwnerVerification) checks.errors.push('Tulajdonos nincs verifikálva');
  if (!checks.notListedPublicly) checks.errors.push('Nyilvánosan hirdetve van');

  checks.passed = checks.errors.length === 0;

  return checks;
};

// =====================================================
// 9. INTRO PRIORITY (Gamification)
// =====================================================

/**
 * Calculate response priority based on speed
 */
export const calculateIntroPriority = async (buyerId, propertyId, responseTime) => {
  // responseTime in minutes
  let priorityPoints = 0;
  let priorityLabel = '';
  let badge = null;

  if (responseTime <= 30) {
    priorityPoints = 100;
    priorityLabel = '⚡ AZONNALI';
    badge = { icon: '⚡', label: 'Villámreakció', color: 'gold' };
  } else if (responseTime <= 2 * 60) {
    priorityPoints = 80;
    priorityLabel = '🔥 GYORS';
    badge = { icon: '🔥', label: 'Gyors reagálás', color: 'orange' };
  } else if (responseTime <= 6 * 60) {
    priorityPoints = 60;
    priorityLabel = '👍 JÓ';
    badge = { icon: '👍', label: 'Jó tempó', color: 'green' };
  } else if (responseTime <= 24 * 60) {
    priorityPoints = 40;
    priorityLabel = '🐌 ÁLAGOS';
    badge = { icon: '🐌', label: 'Átlagos', color: 'gray' };
  } else {
    priorityPoints = 20;
    priorityLabel = '❌ LASSÚ';
    badge = { icon: '❌', label: 'Lassú', color: 'red' };
  }

  // Save priority
  await addDoc(collection(db, 'intro_priorities'), {
    buyerId,
    propertyId,
    responseTime,
    priorityPoints,
    priorityLabel,
    badge,
    createdAt: serverTimestamp()
  });

  // Update buyer stats
  await updateDoc(doc(db, 'users', buyerId), {
    totalPriorityPoints: increment(priorityPoints),
    quickResponses: responseTime <= 120 ? increment(1) : 0,
    lastResponseTime: responseTime
  });

  return {
    priorityPoints,
    priorityLabel,
    badge,
    position: await getBuyerPriorityRank(buyerId, propertyId),
    message: `🎯 ${priorityLabel} prioritás! ${badge ? badge.label : ''}`
  };
};

const getBuyerPriorityRank = async (buyerId, propertyId) => {
  // Get all priorities for this property and rank buyer
  const q = query(
    collection(db, 'intro_priorities'),
    where('propertyId', '==', propertyId),
    orderBy('priorityPoints', 'desc')
  );

  const snapshot = await getDocs(q);
  const allPriorities = snapshot.docs.map(doc => doc.data());
  const rank = allPriorities.findIndex(p => p.buyerId === buyerId) + 1;

  return rank > 0 ? rank : allPriorities.length + 1;
};

// =====================================================
// 10. CO-DEAL MODE
// =====================================================

export const createCoDeal = async (propertyId, buyerIds, shareType = 'equal') => {
  // Validate all buyers exist
  const buyers = [];
  for (const buyerId of buyerIds) {
    const buyerDoc = await getDoc(doc(db, 'users', buyerId));
    if (buyerDoc.exists()) {
      buyers.push({ id: buyerId, ...buyerDoc.data() });
    }
  }

  if (buyers.length < 2) {
    return { success: false, error: 'Legalább 2 vevő szükséges' };
  }

  // Calculate shares
  const shares = calculateShares(buyers, shareType);

  const coDealData = {
    propertyId,
    buyerIds,
    buyers: buyers.map(b => ({ id: b.id, name: b.fullName, email: b.email })),
    shareType,
    shares,
    status: 'pending',
    createdAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days to confirm
    isActive: true,
    type: 'co_deal'
  };

  const coDealRef = await addDoc(collection(db, 'co_deals'), coDealData);

  // Update property
  await updateDoc(doc(db, 'properties', propertyId), {
    coDealMode: true,
    coDealId: coDealRef.id,
    coDealBuyers: buyerIds
  });

  // Notify all buyers
  for (const buyer of buyers) {
    await addDoc(collection(db, 'notifications'), {
      userId: buyer.id,
      type: 'co_deal_invitation',
      coDealId: coDealRef.id,
      propertyId,
      title: '🤝 Co-Deal meghívás!',
      message: `${buyers.length} partnerrel együtt vinnétek ezt a deal-t`,
      requiresAction: true,
      createdAt: serverTimestamp()
    });
  }

  return {
    success: true,
    coDealId: coDealRef.id,
    shares,
    message: `🤝 Co-Deal létrehozva ${buyers.length} partnerrel!`
  };
};

const calculateShares = (buyers, type) => {
  const count = buyers.length;
  
  if (type === 'equal') {
    const share = Math.round(100 / count);
    return buyers.map(b => ({ buyerId: b.id, share, type: 'equal' }));
  }
  
  // Other share types could be implemented
  return buyers.map(b => ({ buyerId: b.id, share: Math.round(100 / count), type }));
};

// =====================================================
// 11. SOFT LISTING MODE
// =====================================================

/**
 * Create stealth/soft listing - only visible to selected buyers
 */
export const createSoftListing = async (propertyData, sellerId, viewerIds) => {
  const softListingData = {
    ...propertyData,
    sellerId,
    viewerIds,
    visibility: 'soft',
    isPublic: false,
    isStealth: true,
    createdAt: serverTimestamp(),
    expiresAt: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days stealth
    viewCount: 0,
    intentCount: 0,
    status: 'soft_listed'
  };

  const listingRef = await addDoc(collection(db, 'soft_listings'), softListingData);

  // Notify selected viewers
  for (const viewerId of viewerIds) {
    await addDoc(collection(db, 'notifications'), {
      userId: viewerId,
      type: 'soft_listing',
      listingId: listingRef.id,
      title: '💎 Soft Listing elérhető!',
      message: 'Kizárólag te és 2-3 másik befektető látja ezt most. Titkos deal!',
      createdAt: serverTimestamp(),
      priority: 'high'
    });
  }

  return {
    success: true,
    listingId: listingRef.id,
    message: '🔒 Soft listing létrehozva - csak kiválasztottak látják!'
  };
};

// =====================================================
// EXPORT ALL FUNCTIONS
// =====================================================

export default {
  // 1. Deal Readiness
  calculateDealReadinessScore,
  
  // 2. HOT DEAL
  detectHotDeal,
  markAsHotDeal,
  
  // 3. Deal Match
  calculateDealMatchScore,
  
  // 4. Intent
  recordIntent,
  INTENT_LEVELS,
  getPrioritizedBuyers,
  
  // 5. First Look
  grantFirstLookAccess,
  checkFirstLookAccess,
  
  // 6. Risk
  analyzePropertyRisk,
  RISK_TYPES,
  
  // 7. Alerts
  createDealAlert,
  
  // 8. Off-Market
  certifyOffMarket,
  
  // 9. Intro Priority
  calculateIntroPriority,
  
  // 10. Co-Deal
  createCoDeal,
  
  // 11. Soft Listing
  createSoftListing
};
